import { useCallback, useEffect, useRef, useState, type PointerEvent } from "react";
import { useScrollContext } from "../../../hooks/context/useScrollContext";
import { cn } from "../../../../../../lib/utils";

const MIN_THUMB_SIZE = 24;
const HIDE_DELAY = 800;

type Axis = "x" | "y";

interface Metrics {
    clientWidth: number;
    clientHeight: number;
    scrollWidth: number;
    scrollHeight: number;
    scrollLeft: number;
    scrollTop: number;
}

const EMPTY_METRICS: Metrics = {
    clientWidth: 0,
    clientHeight: 0,
    scrollWidth: 0,
    scrollHeight: 0,
    scrollLeft: 0,
    scrollTop: 0,
};

const getThumb = (client: number, scroll: number, offset: number) => {
    const maxScroll = scroll - client;
    if (maxScroll <= 0) return null;
    const size = Math.max(MIN_THUMB_SIZE, (client * client) / scroll);
    const position = (offset / maxScroll) * (client - size);
    // px of content scrolled per px of thumb moved
    const ratio = maxScroll / (client - size);
    return { size, position, ratio };
};

/**
 * The grid's native scrollbars are hidden (see .schedule-grid-container in style.scss) so they
 * take no space. This draws thin scrollbars on top of the grid instead: they appear while the
 * pointer is over the grid or while scrolling, fade out shortly after, and the thumbs can be dragged.
 */
function ScrollbarOverlay() {
    const { gridElement } = useScrollContext();
    const [metrics, setMetrics] = useState<Metrics>(EMPTY_METRICS);
    const [isScrolling, setIsScrolling] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isGridHovered, setIsGridHovered] = useState(false);
    const [dragAxis, setDragAxis] = useState<Axis | null>(null);

    const frameRef = useRef<number | null>(null);
    const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const dragStartRef = useRef({ pointer: 0, scroll: 0, ratio: 1 });

    const measure = useCallback(() => {
        if (!gridElement) return;
        if (frameRef.current) cancelAnimationFrame(frameRef.current);
        frameRef.current = requestAnimationFrame(() => {
            frameRef.current = null;
            setMetrics({
                clientWidth: gridElement.clientWidth,
                clientHeight: gridElement.clientHeight,
                scrollWidth: gridElement.scrollWidth,
                scrollHeight: gridElement.scrollHeight,
                scrollLeft: gridElement.scrollLeft,
                scrollTop: gridElement.scrollTop,
            });
        });
    }, [gridElement]);

    useEffect(() => {
        if (!gridElement) return;

        const handleScroll = () => {
            measure();
            setIsScrolling(true);
            if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = setTimeout(() => setIsScrolling(false), HIDE_DELAY);
        };

        const handlePointerEnter = () => {
            measure();
            setIsGridHovered(true);
        };
        const handlePointerLeave = () => setIsGridHovered(false);

        const resizeObserver = new ResizeObserver(measure);
        resizeObserver.observe(gridElement);
        if (gridElement.firstElementChild) resizeObserver.observe(gridElement.firstElementChild);

        gridElement.addEventListener("scroll", handleScroll, { passive: true });
        gridElement.addEventListener("pointerenter", handlePointerEnter);
        gridElement.addEventListener("pointerleave", handlePointerLeave);
        measure();

        return () => {
            gridElement.removeEventListener("scroll", handleScroll);
            gridElement.removeEventListener("pointerenter", handlePointerEnter);
            gridElement.removeEventListener("pointerleave", handlePointerLeave);
            resizeObserver.disconnect();
            if (frameRef.current) cancelAnimationFrame(frameRef.current);
            if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
        };
    }, [gridElement, measure]);

    const vertical = getThumb(metrics.clientHeight, metrics.scrollHeight, metrics.scrollTop);
    const horizontal = getThumb(metrics.clientWidth, metrics.scrollWidth, metrics.scrollLeft);
    const isVisible = isScrolling || isGridHovered || isHovered || dragAxis !== null;

    const handlePointerDown = (axis: Axis) => (e: PointerEvent<HTMLDivElement>) => {
        const thumb = axis === "y" ? vertical : horizontal;
        if (!gridElement || !thumb) return;
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        dragStartRef.current = {
            pointer: axis === "y" ? e.clientY : e.clientX,
            scroll: axis === "y" ? gridElement.scrollTop : gridElement.scrollLeft,
            ratio: thumb.ratio,
        };
        setDragAxis(axis);
    };

    const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
        if (!gridElement || !dragAxis) return;
        const { pointer, scroll, ratio } = dragStartRef.current;
        const delta = (dragAxis === "y" ? e.clientY : e.clientX) - pointer;
        if (dragAxis === "y") gridElement.scrollTop = scroll + delta * ratio;
        else gridElement.scrollLeft = scroll + delta * ratio;
    };

    const handlePointerUp = (e: PointerEvent<HTMLDivElement>) => {
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
        setDragAxis(null);
    };

    if (!vertical && !horizontal) return null;

    const thumbProps = (axis: Axis) => ({
        onPointerDown: handlePointerDown(axis),
        onPointerMove: handlePointerMove,
        onPointerUp: handlePointerUp,
        onPointerCancel: handlePointerUp,
        onPointerEnter: () => setIsHovered(true),
        onPointerLeave: () => setIsHovered(false),
    });

    return (
        <div className={cn("schedule-scrollbar-overlay", isVisible && "is-visible")}>
            {vertical && (
                <div
                    className={cn("schedule-scrollbar-thumb is-vertical", dragAxis === "y" && "is-dragging")}
                    style={{ height: vertical.size, transform: `translateY(${vertical.position}px)` }}
                    {...thumbProps("y")}
                />
            )}
            {horizontal && (
                <div
                    className={cn("schedule-scrollbar-thumb is-horizontal", dragAxis === "x" && "is-dragging")}
                    style={{ width: horizontal.size, transform: `translateX(${horizontal.position}px)` }}
                    {...thumbProps("x")}
                />
            )}
        </div>
    );
}

export default ScrollbarOverlay;
